import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AvailableOption } from '@tsapiclient/zenauto.vehiclequotation.ts.client/interfaces/availableoption';
import { OptionPrice } from '@tsapiclient/zenauto.vehiclequotation.ts.client/interfaces/optionprice';
import { OptionPrices } from '@tsapiclient/zenauto.vehiclequotation.ts.client/interfaces/optionprices';
import { VehicleOption } from '@tsapiclient/zenauto.vehiclequotation.ts.client/interfaces/vehicleoption';
import { OptionType } from '../../enums';
import { CustomisationOption, CustomiseOptions } from 'interface';
import type { RootState } from '../store';

export const initialState: CustomiseOptions = {
  options: [],
  colours: {
    hood: [],
    paint: [],
    trim: [],
  },
};

const updateAvailableOptionPrices = (
  options: AvailableOption[],
  updatedPrices: OptionPrice[]
) => {
  options.forEach((e) => {
    const updatedPrice = updatedPrices.find((f) => f.id === e.id);

    if (updatedPrice) {
      e.price = updatedPrice.price;
      e.monthlyRentalCost = updatedPrice.monthlyRentalCost;
    }
  });
};

const updateOptionPrices = (
  options: VehicleOption[],
  updatedPrices: OptionPrice[]
) => {
  options.forEach((e) =>
    e.items.forEach((i) => {
      const updatedPrice = updatedPrices.find((f) => f.id === i.id);

      if (updatedPrice) {
        i.price = updatedPrice.price;
        i.monthlyCost = updatedPrice.monthlyRentalCost;
      }
    })
  );
};

const updateColourOption = (
  availableOptions: AvailableOption[],
  optionId: number,
  checked: boolean
) => {
  const option = availableOptions.find((f) => f.id === optionId);

  if (option) {
    option.checked = checked;
  }
};

export const selectedOptionsSlice = createSlice({
  name: 'selectedOptions',
  initialState: initialState,
  reducers: {
    setInitialState: (state, action: PayloadAction<CustomiseOptions>) => {
      state.colours = action.payload.colours;
      state.options = action.payload.options;
    },
    updateTrim: (state, action: PayloadAction<AvailableOption>) => {
      state.colours.trim.map((t) => (t.checked = false));
      state.colours.trim
        .filter((trim) => trim.id === action.payload.id)
        .map((x) => (x.checked = action.payload.checked));
    },
    updateHood: (state, action: PayloadAction<AvailableOption>) => {
      state.colours.hood.map((t) => (t.checked = false));
      state.colours.hood
        .filter((hood) => hood.id === action.payload.id)
        .map((x) => (x.checked = action.payload.checked));
    },
    updateColour: (state, action: PayloadAction<AvailableOption>) => {
      state.colours.paint.map((t) => (t.checked = false));
      state.colours.paint
        .filter((paint) => paint.id === action.payload.id)
        .map((x) => (x.checked = action.payload.checked));
    },
    updateOption: (state, action: PayloadAction<number>) => {
      state.options
        .map((value) => value.items.map((child) => child))
        .reduce(
          (l, n) => l.concat(n.filter((c) => c.id === action.payload)),
          []
        )
        .map((x) => {
          x.checked = true;

          if (x.optionRules && x.optionRules.length > 0) {
            x.optionRules.forEach((e) => (e.checked = true));
          }
        });
    },
    removeOption: (state, action: PayloadAction<number>) => {
      state.options
        .map((value) => value.items.map((child) => child))
        .reduce(
          (l, n) => l.concat(n.filter((c) => c.id === action.payload)),
          []
        )
        .map((x) => {
          x.checked = false;

          if (x.optionRules && x.optionRules.length > 0) {
            x.optionRules.forEach((e) => (e.checked = false));
          }
        });
    },
    updateOptions: (state, action: PayloadAction<CustomisationOption[]>) => {
      action.payload.forEach((e) => {
        switch (e.optionType) {
          case OptionType.paint:
            updateColourOption(state.colours.paint, e.optionId, e.checked);
            break;
          case OptionType.trim:
            updateColourOption(state.colours.trim, e.optionId, e.checked);
            break;
          case OptionType.hood:
            updateColourOption(state.colours.hood, e.optionId, e.checked);
            break;
          case OptionType.option:
            const option = state.options
              .find((f) => f.name === e.title)
              ?.items.find((f) => f.id === e.optionId);

            if (option) {
              option.checked = e.checked;

              if (option.optionRules && option.optionRules.length > 0) {
                option.optionRules.forEach((d) => (d.checked = e.checked));
              }
            }
        }
      });
    },
    updateSelectedOptionPrices: (
      state,
      action: PayloadAction<OptionPrices>
    ) => {
      updateAvailableOptionPrices(
        state.colours.hood,
        action.payload.colourOptionPrice.hood
      );
      updateAvailableOptionPrices(
        state.colours.paint,
        action.payload.colourOptionPrice.paint
      );
      updateAvailableOptionPrices(
        state.colours.trim,
        action.payload.colourOptionPrice.trim
      );
      updateOptionPrices(state.options, action.payload.optionPrices);
    },
  },
});

export const {
  setInitialState,
  updateTrim,
  updateHood,
  updateColour,
  updateOption,
  removeOption,
  updateOptions,
  updateSelectedOptionPrices,
} = selectedOptionsSlice.actions;

export const selectedOptions = (state: RootState) => state.selectedOptions;

export default selectedOptionsSlice.reducer;
